import assert from '@banking/ui/utils/assert';
import workflow from '@banking/ui/lib/workflow';
import { Card, ListCardsRequest, TerminateCardRequest } from '@square/dashboard-protos/models/squareup/bbdebit/cardmanagement';
import { GetCustomizationSettingsRequest } from '@square/dashboard-protos/models/squareup/bizbank/service';
import { GlobalAddress } from '@square/dashboard-protos/models/squareup/common/location';
import { getBusinessName, getCardholder } from '@banking/ui/utils/card/card';
import { SignatureFont } from '@banking/ui/models/common/card-customization';
import * as addressValidationWorkflow from '@banking/ui/workflows/common/address-validation';
import * as cardCustomizationWorkflow from '@banking/ui/workflows/common/card-customization';
import * as twoFactorWorkflow from '@banking/ui/workflows/common/two-factor';
import { AccountHolderCardScreen, ActionType as AccountHolderCardActionType, update as accountHolderCardUpdate, createCardAction, listCardsReceivedAction, listCardsRejectedAction, userClickCancellationReasonAction } from './account-holder-card';
import getPostalCodeFormatter from '@banking/ui/utils/field-kit-formatters/postal-code';
import { createState as createTwoFactorState } from '@banking/ui/workflows/common/two-factor';
import { decrementNavigation, incrementNavigation } from '@banking/ui/workflows/common/utils';
export let AuthorizedUserCardScreen = /*#__PURE__*/function (AuthorizedUserCardScreen) {
  AuthorizedUserCardScreen["ConfirmCancellation"] = "AUTHORIZED_USER_CARD_SCREEN_CONFIRM_CANCELLATION";
  return AuthorizedUserCardScreen;
}({});
export let Screen = /*#__PURE__*/function (Screen) {
  Screen["None"] = "SCREEN_NONE";
  Screen["Success"] = "SCREEN_SUCCESS";
  Screen["Error"] = "SCREEN_ERROR";
  return Screen;
}({});
export const TWO_FACTOR_FLOW = [AccountHolderCardScreen.TwoFactorPhoneNumber, AccountHolderCardScreen.TwoFactorVerifyCode];
export const REFETCH_CARD_AFTER_MODAL_CLOSES_SCREENS = [Screen.Success, AccountHolderCardScreen.OwnCardsDeactivationSuccess, AccountHolderCardScreen.CardReorderSuccess, AccountHolderCardScreen.SendFeedback,
// The backend occasionally throws a timeout error even when the action succeeds.
// In such cases, we should refresh the card state to display the latest status. This prevents scenarios
// where a card is terminated, but the user can still call irrelevant actions like "Lock Card".
// It also addresses an edge case during card reordering where TerminateCard succeeds but CreateCard fails.
// Since the card is deactivated in that scenario, we should refetch the card.
Screen.Error];
export const REORDER_SCREENS_AFTER_ADDRESS_AND_CUSTOMIZATION = [AccountHolderCardScreen.ConfirmReplacement, AccountHolderCardScreen.CardReorderSuccess];
export const CANCELLATION_FLOW_ACCOUNT_HOLDER_CARD = [AccountHolderCardScreen.CancellationReasonSelection, AccountHolderCardScreen.ReplacementCustomizationSelection, AccountHolderCardScreen.ConfirmShippingAddress, ...REORDER_SCREENS_AFTER_ADDRESS_AND_CUSTOMIZATION];
export const DEACTIVATION_FLOW_ACCOUNT_HOLDER_CARD = [AccountHolderCardScreen.CancellationReasonSelection, AccountHolderCardScreen.ConfirmDeactivation, AccountHolderCardScreen.SendFeedback, Screen.Success];
export const ACCOUNT_CLOSURE_FLOW_WITHOUT_CANCELLATION_REASON_SELECTION = [AccountHolderCardScreen.AccountClosureOrCardDeactivationSelection, AccountHolderCardScreen.ConfirmAccountClosure, AccountHolderCardScreen.SendFeedback, AccountHolderCardScreen.AccountClosed];
export const ACCOUNT_CLOSURE_FLOW_ACCOUNT_HOLDER_CARD = [AccountHolderCardScreen.CancellationReasonSelection, ...ACCOUNT_CLOSURE_FLOW_WITHOUT_CANCELLATION_REASON_SELECTION];
export const DEACTIVATE_OWN_CARDS_FLOW_WITHOUT_CANCELLATION_REASON_SELECTION = [AccountHolderCardScreen.AccountClosureOrCardDeactivationSelection, AccountHolderCardScreen.ConfirmOwnCardsDeactivation, AccountHolderCardScreen.SendFeedback, AccountHolderCardScreen.OwnCardsDeactivationSuccess];
export const DEACTIVATE_OWN_CARDS_FLOW_ACCOUNT_HOLDER_CARD = [AccountHolderCardScreen.CancellationReasonSelection, ...DEACTIVATE_OWN_CARDS_FLOW_WITHOUT_CANCELLATION_REASON_SELECTION];
export const CANCELLATION_FLOW_AUTHORIZED_USER_CARD = [AuthorizedUserCardScreen.ConfirmCancellation, Screen.Success];
export const DOCUMENT_BASED_IDV_FLOW = [AccountHolderCardScreen.DocumentBasedIdvConsent, AccountHolderCardScreen.DocumentBasedIdvPhoneNumberInput, AccountHolderCardScreen.DocumentBasedIdvInstructions, AccountHolderCardScreen.DocumentBasedIdvResendLink];
export const SUCCESS_SCREENS = [Screen.Success, AccountHolderCardScreen.OwnCardsDeactivationSuccess, AccountHolderCardScreen.CardReorderSuccess, AccountHolderCardScreen.AccountClosed];
// Type of card cancellation to use to determine deactivation flow
export let CancellationType = /*#__PURE__*/function (CancellationType) {
  CancellationType["AccountHolderCard"] = "CANCEL_CARD_TYPE_ACCOUNT_HOLDER_CARD";
  CancellationType["AuthorizedUserCard"] = "CANCEL_CARD_TYPE_AUTHORIZED_USER_CARD";
  CancellationType["AccountHolderAccount"] = "CANCEL_CARD_TYPE_ACCOUNT_HOLDER_ACCOUNT";
  CancellationType["AccountHolderNeverReceivedCard"] = "CANCEL_CARD_TYPE_NEVER_RECEIVED_CARD";
  return CancellationType;
}({});
export const INITIAL_STATE = {
  accountClosed: false,
  accountOwnerCards: [],
  addressValidation: addressValidationWorkflow.createState(),
  balanceId: '',
  businessName: '',
  cancellationType: CancellationType.AuthorizedUserCard,
  cardBrand: '',
  cardCustomization: null,
  customizationSettings: null,
  cardCustomizationId: '',
  cardHolder: '',
  cardToken: '',
  cards: [],
  countryAddressData: null,
  hasUpdatedBusinessInfo: false,
  lastFour: '',
  loading: false,
  modalOpen: false,
  navigation: {
    flow: [],
    current: Screen.None
  },
  newCustomizationActive: false,
  phoneNumber: '',
  phoneNumberError: '',
  reorderedCardCustomizationId: null,
  signatureFont: SignatureFont.AlexBrush,
  signatureText: '',
  reason: TerminateCardRequest.TerminateCardReason.TERMINATE_CARD_REASON_CANCEL,
  replace: false,
  shippingAddress: GlobalAddress.create(),
  updatedShippingAddress: GlobalAddress.create(),
  unitToken: '',
  twoFactor: createTwoFactorState()
};
export const createState = (overrides = {}) => ({
  ...INITIAL_STATE,
  ...overrides
});
export let ActionType = /*#__PURE__*/function (ActionType) {
  ActionType["Initialize"] = "CARD_MANAGEMENT/CANCEL_CARD/INITIALIZE";
  ActionType["GetCustomizationSettingsReceived"] = "CARD_MANAGEMENT/CANCEL_CARD/GET_CUSTOMIZATION_SETTINGS_RECEIVED";
  ActionType["GetCustomizationSettingsRejected"] = "CARD_MANAGEMENT/CANCEL_CARD/GET_CUSTOMIZATION_SETTINGS_REJECTED";
  ActionType["TerminateCardReceived"] = "CARD_MANAGEMENT/CANCEL_CARD/TERMINATE_CARD_RECEIVED";
  ActionType["TerminateCardRejected"] = "CARD_MANAGEMENT/CANCEL_CARD/TERMINATE_CARD_REJECTED";
  ActionType["UserClickBack"] = "CARD_MANAGEMENT/CANCEL_CARD/USER_CLICK_BACK";
  ActionType["UserClickCloseModal"] = "CARD_MANAGEMENT/CANCEL_CARD/USER_CLICK_CLOSE_MODAL";
  ActionType["UserClickConfirmCancellation"] = "CARD_MANAGEMENT/CANCEL_CARD/USER_CLICK_CONFIRM_CANCELLATION";
  ActionType["UserClickNext"] = "CARD_MANAGEMENT/CANCEL_CARD/USER_CLICK_NEXT";
  return ActionType;
}({});
export const initializeAction = (balanceId, card, cardCustomization, cancellationType, unitToken) => ({
  type: ActionType.Initialize,
  balanceId,
  card,
  cardCustomization,
  cancellationType,
  unitToken
});
export const getCustomizationSettingsReceivedAction = response => ({
  type: ActionType.GetCustomizationSettingsReceived,
  response
});
export const getCustomizationSettingsRejectedAction = error => ({
  type: ActionType.GetCustomizationSettingsRejected,
  error
});
export const terminateCardReceivedAction = response => ({
  type: ActionType.TerminateCardReceived,
  response
});
export const terminateCardRejectedAction = error => ({
  type: ActionType.TerminateCardRejected,
  error
});
export const userClickBackAction = () => ({
  type: ActionType.UserClickBack
});
export const userClickCloseModalAction = () => ({
  type: ActionType.UserClickCloseModal
});
export const userClickConfirmCancellationAction = (replace = false) => ({
  type: ActionType.UserClickConfirmCancellation,
  replace
});
export const userClickNextAction = () => ({
  type: ActionType.UserClickNext
});
function handleInitialize(context, state, action) {
  const {
    balanceId,
    cancellationType,
    card,
    cardCustomization,
    unitToken
  } = action;
  // TODO(tugce): Backend does not return balance id yet
  // assert(
  //   balanceId !== '',
  //   'Card settings/Cancel card - Balance id cannot be empty'
  // );
  assert(card.id !== '', 'Card settings/Cancel card - Card id cannot be empty');
  // NOTE(cvu): currently hardcoding Mastercard but good practice to assert for this until better support
  assert(card.cardBrand, 'Card settings/Cancel card - Card brand cannot be null');
  assert(card.lastFour !== '', 'Card settings/Cancel card - Card brand cannot be empty');
  const cardHolder = getCardholder(card);
  assert(cardHolder !== '', 'Card settings/Cancel card - Card holder does not exist');
  const isDigitalCard = card.cardType === Card.CardType.CARD_TYPE_DIGITAL;
  if (!isDigitalCard) {
    assert(card.fulfillment?.address, 'Card settings/Cancel card - Card fulfillment address does not exist');
    assert(card.fulfillment?.customization?.[0]?.id, 'Card settings/Cancel card - Card customization id does not exist');
  }
  assert(unitToken, 'Card settings/Cancel card - unit token does not exist');
  const businessName = getBusinessName(card);
  const fulfillmentAddress = card.fulfillment?.address;
  const parsedFulfillmentAddress = fulfillmentAddress ? GlobalAddress.create({
    ...fulfillmentAddress,
    postalCode: getPostalCodeFormatter(fulfillmentAddress.countryCode).parse(fulfillmentAddress.postalCode).trim()
  }) : GlobalAddress.create();
  const newState = {
    ...state,
    balanceId,
    businessName,
    cancellationType,
    cardBrand: 'Mastercard',
    // TODO(cvu): support other card brands
    cardCustomization,
    cardCustomizationId: card.fulfillment?.customization?.[0]?.id ?? '',
    cardHolder,
    cardToken: card.id,
    lastFour: card.lastFour,
    modalOpen: true,
    shippingAddress: parsedFulfillmentAddress,
    signatureText: cardHolder,
    unitToken,
    updatedShippingAddress: parsedFulfillmentAddress
  };
  switch (cancellationType) {
    case CancellationType.AccountHolderCard:
      return [{
        ...newState,
        loading: true,
        navigation: {
          flow: CANCELLATION_FLOW_ACCOUNT_HOLDER_CARD,
          current: CANCELLATION_FLOW_ACCOUNT_HOLDER_CARD[0]
        }
      }, () =>
      // call customization setting to get most up-to-date business name
      // in case we need it for reorder
      context.getCustomizationSettings(GetCustomizationSettingsRequest.create({
        unitToken
      })).then(getCustomizationSettingsReceivedAction, getCustomizationSettingsRejectedAction)];
    case CancellationType.AccountHolderNeverReceivedCard:
      return [{
        ...newState,
        loading: true,
        navigation: {
          flow: CANCELLATION_FLOW_ACCOUNT_HOLDER_CARD,
          current: CANCELLATION_FLOW_ACCOUNT_HOLDER_CARD[0]
        }
      }, () => {
        // call customization setting to get most up-to-date business name
        // in case we need it for reorder
        return context.getCustomizationSettings(GetCustomizationSettingsRequest.create({
          unitToken
        })).then(getCustomizationSettingsReceivedAction, getCustomizationSettingsRejectedAction).then(() => {
          // After customization settings are fetched, return the cancellation reason action
          return userClickCancellationReasonAction(TerminateCardRequest.TerminateCardReason.TERMINATE_CARD_REASON_CARD_NOT_RECEIVED);
        });
      }];
    case CancellationType.AuthorizedUserCard:
      return [{
        ...newState,
        navigation: {
          flow: CANCELLATION_FLOW_AUTHORIZED_USER_CARD,
          current: CANCELLATION_FLOW_AUTHORIZED_USER_CARD[0]
        }
      }, null];
    case CancellationType.AccountHolderAccount:
      return [{
        ...newState,
        loading: true,
        navigation: {
          flow: ACCOUNT_CLOSURE_FLOW_WITHOUT_CANCELLATION_REASON_SELECTION,
          current: ACCOUNT_CLOSURE_FLOW_WITHOUT_CANCELLATION_REASON_SELECTION[0]
        }
      }, () => context.listCards(ListCardsRequest.create({
        balanceId
      })).then(listCardsReceivedAction, listCardsRejectedAction)];
  }
}
function handleGetCustomizationSettingsReceived(state, action) {
  const customizationSettings = action.response;
  const upToDateBusinessName = customizationSettings?.businessName || state.businessName;
  const hasUpdatedBusinessInfo = customizationSettings.businessName !== state.businessName;
  return [{
    ...state,
    businessName: upToDateBusinessName,
    loading: false,
    hasUpdatedBusinessInfo,
    customizationSettings
  }, null];
}
function handleGetCustomizationSettingsRejected(context, state, action) {
  const {
    error
  } = action;
  context.sentry.captureException(`Get customization settings - ${error}`);
  return [{
    ...state,
    loading: false,
    navigation: {
      flow: [Screen.Error],
      current: Screen.Error
    }
  }, null];
}
function handleTerminateCardReceived(context, state, action) {
  const {
    response
  } = action;
  if (response.errors.length > 0) {
    context.sentry.captureException(`Cancel card - TerminateCard errors: ${response.errors}`);
    return [{
      ...state,
      loading: false,
      navigation: {
        flow: [Screen.Error],
        current: Screen.Error
      }
    }, null];
  }
  if (state.replace) {
    return [{
      ...state,
      loading: true
    }, createCardAction];
  }
  return [{
    ...state,
    loading: false,
    navigation: incrementNavigation(state.navigation)
  }, null];
}
function handleTerminateCardRejected(context, state, action) {
  const {
    error
  } = action;
  context.sentry.captureException(`Cancel card - ${error}`);
  return [{
    ...state,
    loading: false,
    navigation: {
      flow: [Screen.Error],
      current: Screen.Error
    }
  }, null];
}
function handleUserClickBack(state) {
  const {
    navigation
  } = state;
  // if at the beginning of the flow, close the modal
  if (navigation.current === navigation.flow[0]) {
    return [state, () => userClickCloseModalAction()];
  }

  // Removes one-off screens added to the flow on back
  switch (navigation.current) {
    case AccountHolderCardScreen.Customization:
    case AccountHolderCardScreen.EditShippingAddress:
    case AccountHolderCardScreen.NormalizeAddress:
      {
        const updatedFlow = navigation.flow.reduce((acc, screen) => {
          if (screen === navigation.current) {
            return acc;
          }
          acc.push(screen);
          return acc;
        }, []);
        return [{
          ...state,
          navigation: {
            ...decrementNavigation(navigation),
            flow: updatedFlow
          }
        }, null];
      }
  }
  return [{
    ...state,
    navigation: decrementNavigation(navigation)
  }, null];
}
function handleUserClickCloseModal(context, state) {
  if (state.accountClosed) {
    context.applicationRouter.transitionToExternal('balance-applet-checking', {
      queryParams: {
        unitToken: state.unitToken
      }
    });
  }
  return [{
    ...state,
    modalOpen: false
  }, null];
}
function handleUserClickConfirmCancellation(context, state, action) {
  const {
    replace
  } = action;
  const work = () => context.terminateCard(TerminateCardRequest.create({
    cardToken: state.cardToken,
    reason: state.reason
  })).then(terminateCardReceivedAction, terminateCardRejectedAction);
  return [{
    ...state,
    loading: true,
    replace
  }, work];
}
function handleUserClickNext(state) {
  return [{
    ...state,
    navigation: incrementNavigation(state.navigation)
  }, null];
}
export const update = context =>
// eslint-disable-next-line complexity
(state, action) => {
  switch (action.type) {
    case ActionType.Initialize:
      return handleInitialize(context, state, action);
    case ActionType.GetCustomizationSettingsReceived:
      return handleGetCustomizationSettingsReceived(state, action);
    case ActionType.GetCustomizationSettingsRejected:
      return handleGetCustomizationSettingsRejected(context, state, action);
    case ActionType.TerminateCardReceived:
      return handleTerminateCardReceived(context, state, action);
    case ActionType.TerminateCardRejected:
      return handleTerminateCardRejected(context, state, action);
    case ActionType.UserClickBack:
      return handleUserClickBack(state);
    case ActionType.UserClickCloseModal:
      return handleUserClickCloseModal(context, state);
    case ActionType.UserClickConfirmCancellation:
      return handleUserClickConfirmCancellation(context, state, action);
    case ActionType.UserClickNext:
      return handleUserClickNext(state);
    case addressValidationWorkflow.ActionType.ChosenAddressChange:
    case addressValidationWorkflow.ActionType.NormalizedAddressChange:
    case addressValidationWorkflow.ActionType.ValidateAddress:
    case addressValidationWorkflow.ActionType.ValidateAddressReceived:
    case addressValidationWorkflow.ActionType.ValidateAddressRejected:
    case addressValidationWorkflow.ActionType.ValidationCompleted:
    case cardCustomizationWorkflow.ActionType.NewCustomizationActiveChange:
    case cardCustomizationWorkflow.ActionType.SignatureFontChange:
    case cardCustomizationWorkflow.ActionType.SignatureTextChange:
    case twoFactorWorkflow.ActionType.IncrementNavigation:
    case twoFactorWorkflow.ActionType.UserChangePhoneNumber:
    case twoFactorWorkflow.ActionType.UserChangeVerificationCode:
    case twoFactorWorkflow.ActionType.UserClickEnroll:
    case twoFactorWorkflow.ActionType.UserClickVerify:
    case twoFactorWorkflow.ActionType.UserClickResendCode:
    case twoFactorWorkflow.ActionType.PhoneNumberEnrollTwoFactorReceived:
    case twoFactorWorkflow.ActionType.PhoneNumberEnrollTwoFactorError:
    case twoFactorWorkflow.ActionType.VerifyCodeEnrollTwoFactorReceived:
    case twoFactorWorkflow.ActionType.VerifyCodeEnrollTwoFactorError:
    case twoFactorWorkflow.ActionType.ToggleButtonLoading:
    case AccountHolderCardActionType.CountryAddressDataReceived:
    case AccountHolderCardActionType.CountryAddressDataRejected:
    case AccountHolderCardActionType.CreateCard:
    case AccountHolderCardActionType.CreateCardCustomizationReceived:
    case AccountHolderCardActionType.CreateCardCustomizationRejected:
    case AccountHolderCardActionType.CreateCardReceived:
    case AccountHolderCardActionType.CreateCardRejected:
    case AccountHolderCardActionType.DeactivateAccountReceived:
    case AccountHolderCardActionType.DeactivateAccountRejected:
    case AccountHolderCardActionType.DeactivateOwnCardsReceived:
    case AccountHolderCardActionType.DeactivateOwnCardsRejected:
    case AccountHolderCardActionType.ListCardsReceived:
    case AccountHolderCardActionType.ListCardsRejected:
    case AccountHolderCardActionType.MultipassStatusReceived:
    case AccountHolderCardActionType.MultipassStatusRejected:
    case AccountHolderCardActionType.PhoneNumberChange:
    case AccountHolderCardActionType.ProvideFeedbackReceived:
    case AccountHolderCardActionType.ProvideFeedbackRejected:
    case AccountHolderCardActionType.ShippingAddressChange:
    case AccountHolderCardActionType.StartChallengeReceived:
    case AccountHolderCardActionType.StartChallengeRejected:
    case AccountHolderCardActionType.UserChooseAccountClosureFlow:
    case AccountHolderCardActionType.UserChooseDeactivateOwnCardsFlow:
    case AccountHolderCardActionType.UserClickCancellationReason:
    case AccountHolderCardActionType.UserClickConfirmCardCustomization:
    case AccountHolderCardActionType.UserClickDeactivateAccount:
    case AccountHolderCardActionType.UserClickDeactivateOwnCards:
    case AccountHolderCardActionType.UserClickDesignNewCard:
    case AccountHolderCardActionType.UserClickEditShippingAddress:
    case AccountHolderCardActionType.UserClickOrderReplacementUsingSameDesign:
    case AccountHolderCardActionType.UserClickSendIdvVerificationLink:
    case AccountHolderCardActionType.UserClickStartChallenge:
    case AccountHolderCardActionType.UserClickSendFeedback:
      return accountHolderCardUpdate(context)(state, action);
  }
};
const cancelCard = (seed, context) => {
  const w = workflow(update(context), seed);
  return {
    states: w.states,
    updates: w.updates,
    emit: w.emit
  };
};
export default cancelCard;